import {
  MenuItem,
  MenuItemExtras,
  FormExtras,
  MeetingRoomExtras,
  isFormExtras,
  isMeetingRoomExtras,
} from 'types/menuTypes';

/**
 * Resolve extras from a MenuItem, prioritizing menu.extras (per-item, from connections)
 * over feature.extras (shared across items from Strapi).
 */
export const resolveExtras = (menu: MenuItem | null | undefined): MenuItemExtras | null => {
  if (!menu) return null;
  return menu.extras || menu.feature?.extras || null;
};

/**
 * Resolve form_group_id from a MenuItem's extras chain.
 * Returns null if not found or not a valid string.
 */
export const resolveFormGroupId = (menu: MenuItem | null | undefined): string | null => {
  const extras = resolveExtras(menu);
  if (!extras || !isFormExtras(extras)) return null;
  return extras.form_group_id && typeof extras.form_group_id === 'string'
    ? extras.form_group_id
    : null;
};

/**
 * Resolve form_group_name from a MenuItem's extras chain.
 */
export const resolveFormGroupName = (menu: MenuItem | null | undefined): string | null => {
  const extras = resolveExtras(menu);
  if (!extras || !isFormExtras(extras)) return null;
  return extras.form_group_name || null;
};

/**
 * Resolve meeting room extras from a MenuItem.
 */
export const resolveMeetingRoomExtras = (
  menu: MenuItem | null | undefined
): MeetingRoomExtras | null => {
  const extras = resolveExtras(menu);
  if (!extras || !isMeetingRoomExtras(extras)) return null;
  return extras;
};

/**
 * Get a stable tab key for a MenuItem based on its dynamic_component type.
 * Used by ThirdLevelLayout and SideBar for tab identification.
 */
export const getMenuTabKey = (menu: MenuItem): string => {
  const dynamicComponent = menu.feature?.dynamic_component;

  if (dynamicComponent === 'MEETING_ROOM' || dynamicComponent === 'MEETING_ROOM_ROOM') {
    const extras = resolveMeetingRoomExtras(menu);
    if (extras?.internal_id) return extras.internal_id;
  }

  if (dynamicComponent === 'FORM_DYNAMIC' || dynamicComponent === 'FORM') {
    const groupId = resolveFormGroupId(menu);
    if (groupId) return groupId;
  }

  return menu.feature?.documentId || '';
};
